import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { IFormInput } from '@mwe/models';
import { createBirthDatePickerTimeRange } from '../dates/date.utils';
import { getB2BCorporateRegisterNumberValidators, getB2BNameValidators, getB2BUidNumberValidators } from '../common/form.utils';

type FormInputOverride = Partial<IFormInput> & { initialValue: IFormInput['initialValue'] };
type FormInputFactory<O extends FormInputOverride = FormInputOverride> = (override: O) => IFormInput;
function createOverwritableFormInputFactory(base: Omit<IFormInput, 'initialValue'>): FormInputFactory {
  return (override: FormInputOverride) => {
    return { ...base, ...override, initialValue: override.initialValue ?? '' } as IFormInput;
  };
}

export function trimmedMinLength(minLength: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const badValue = { equal: false };
    if (control.value) {
      return control.value.trim().length >= minLength ? null : badValue;
    }
    return badValue;
  };
}

const birthdayTimeRange = createBirthDatePickerTimeRange();
/** Commonly used FormInputs to reuse */
export const CommonFormInputs: Record<string, FormInputFactory> = {
  salutation: createOverwritableFormInputFactory({
    name: 'mwe-order-personal-data-salutation',
    validators: [Validators.required],
    labelKey: 'newClient.personalDetails.salutation.label',
    placeholder: 'newClient.personalDetails.salutation.placeholder',
    validationErrorLabelKey: 'newClient.personalDetails.salutation.error',
    inputClasses: 'col-md-4',
    validate: true,
    componentType: 'select',
    options: [
      { value: 'Herr', text: 'newClient.personalDetails.salutation.options.H' },
      { value: 'Frau', text: 'newClient.personalDetails.salutation.options.F' },
    ],
  }),
  /** For title make sure to provide the options (enforced by type) */
  title: createOverwritableFormInputFactory({
    name: 'mwe-order-personal-data-title',
    validators: [],
    labelKey: 'newClient.personalDetails.titleInput.label',
    placeholder: 'newClient.personalDetails.titleInput.placeholder',
    inputClasses: 'col-md-8',
    componentType: 'typeahead',
    typeaheadOptions: {
      regex: '^{0}',
    },
  }),
  firstName: createOverwritableFormInputFactory({
    name: 'mwe-order-personal-data-firstname',
    validators: [Validators.required, trimmedMinLength(2)],
    labelKey: 'newClient.personalDetails.firstName.label',
    validationErrorLabelKey: 'newClient.personalDetails.firstName.error',
    validate: true,
    directiveType: 'name',
  }),
  lastName: createOverwritableFormInputFactory({
    name: 'mwe-order-personal-data-lastname',
    validators: [Validators.required, trimmedMinLength(2)],
    labelKey: 'newClient.personalDetails.lastName.label',
    validationErrorLabelKey: 'newClient.personalDetails.lastName.error',
    validate: true,
    directiveType: 'name',
  }),
  birthDate: createOverwritableFormInputFactory({
    name: 'mwe-order-personal-data-birthday',
    labelKey: 'newClient.personalDetails.birthday.label',
    validators: [Validators.required],
    componentType: 'birthdate',
    validationErrorLabelKey: 'newClient.personalDetails.birthday.errors.birthday',
    validationErrorLabelParams: { ...birthdayTimeRange },
    validate: true,
    datepickerConfig: {
      minDate: birthdayTimeRange[0],
      maxDate: birthdayTimeRange[1],
      readonly: true,
      displayFormat: 'DD.MM.YYYY',
    },
  }),
  /** B2B */
  companyName: createOverwritableFormInputFactory({
    name: 'mwe-order-business-data-company-name',
    validators: [Validators.required, trimmedMinLength(2), getB2BNameValidators()],
    labelKey: 'newClient.businessDetails.companyName.label',
    validationErrorLabelKey: 'newClient.businessDetails.companyName.error',
    validate: true,
    infoTextCode: 'kundenInformationen.firmenName.error_firmenname',
    maxLength: 200,
  }),
  uidNumber: createOverwritableFormInputFactory({
    name: 'mwe-order-business-data-uid-number',
    helpLabelKey: 'newClient.businessDetails.uidNumber.helpLabel',
    validate: true,
    validators: [getB2BUidNumberValidators()],
    labelKey: 'newClient.businessDetails.uidNumber.label',
    validationErrorLabelKey: 'newClient.businessDetails.uidNumber.error',
    inputClasses: 'col-12 col-md-6',
  }),
  corporateRegisterNumber: createOverwritableFormInputFactory({
    name: 'mwe-order-business-data-corporate-register-number',
    helpLabelKey: 'newClient.businessDetails.corporateRegisterNumber.helpLabel',
    validate: true,
    validators: [getB2BCorporateRegisterNumberValidators()],
    labelKey: 'newClient.businessDetails.corporateRegisterNumber.label',
    validationErrorLabelKey: 'newClient.businessDetails.corporateRegisterNumber.error',
    inputClasses: 'col-12 col-md-6',
  }),
};
